<script setup lang="ts">
import type { Color } from '@global-types/colors.ts';
import { computed } from 'vue';
import Icon from '@components/Icon.vue';
import Typography from '@components/Typography.vue';
import { IconSparkleFill } from '@icons';

const { item } = defineProps<{
  item: {
    value: string,
    selected: boolean,
    highlighted: boolean,
  };
}>();

const containerClass = computed<string>(() => {
  if (item.selected) {
    return 'border-blue bg-blue-lighter';
  }

  if (item.highlighted) {
    return 'border-purple-light bg-purple-lighter hover:bg-purple-light';
  }

  return 'bg-white border-gray hover:bg-gray-lighter';
});

const inputClass = computed<string>(() => {
  if (item.selected) {
    return '!border-blue';
  }

  if (item.highlighted) {
    return '!border-purple-light';
  }

  return '!border-gray';
});

const textColor = computed<Color>(() => {
  if (item.selected) {
    return 'blue';
  }

  if (item.highlighted) {
    return 'purple';
  }

  return 'black';
});

const emit = defineEmits<{(e: 'checked', payload: string): void }>();
</script>

<template>
  <div
    :class="[
      'flex p-3 lg:h-[130px] lg:w-[140px]',
      'rounded-lg border border-solid',
      'relative cursor-pointer transition-colors',
      containerClass
    ]"
    @click="emit('checked', item.value)"
  >
    <div class="flex w-full lg:flex-col">
      <div class="flex items-start justify-between">
        <input
          type="checkbox"
          :checked="item.selected"
          :class="[
            'ml-1 mr-4 mt-1 size-4 lg:ml-1',
            'rounded border',
            'lg:hover:cursor-pointer',
            inputClass
          ]"
          @click.stop
          @change="emit('checked', item.value)"
        >

        <Icon
          v-if="item.highlighted"
          class="absolute right-3 lg:block"
          :svg="IconSparkleFill"
          width="16"
          height="16"
          fill="purple"
        />
      </div>

      <div class="flex flex-col gap-y-1 lg:mt-4">
        <slot name="icon" />

        <Typography
          variant="body-small-bold"
          :color="textColor"
          class="text-wrap"
        >
          <slot name="label" />
        </Typography>
      </div>
    </div>
  </div>
</template>
